import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
    Activity,
    AlertTriangle,
    CheckCircle2,
    ChevronRight,
    Clock,
    FileWarning,
    Loader2,
    Play,
    Radar,
    RefreshCw,
    Shield,
    Sparkles,
    Target,
    Users,
    XCircle,
    Zap,
    ListChecks,
    Car,
    HeartPulse,
    Search,
    Radio,
    Crosshair,
    Lightbulb,
    History,
    ExternalLink,
    Filter,
} from "lucide-react";
import { toast } from "sonner";
import { supremeOps } from "@/lib/supremeOpsApi";
import DosyaIntelPanel from "@/components/ai/DosyaIntelPanel";
import { Button } from "@/components/ui/button";

const TABS = [
    { id: "komuta", label: "Komuta", icon: Radar },
    { id: "onay", label: "Onay Kapısı", icon: Shield },
    { id: "oncelik", label: "Öncelik Radarı", icon: Target },
    { id: "istihbarat", label: "İstihbarat", icon: Radio },
    { id: "warroom", label: "Savaş Odası", icon: Crosshair },
    { id: "toplu", label: "Toplu İşlem", icon: Zap },
    { id: "saglik", label: "Dosya Sağlığı", icon: HeartPulse },
];

const BAND_META = {
    acil: { label: "Acil", cls: "border-rose-500/40 bg-rose-500/10 text-rose-300", dot: "bg-rose-500" },
    bugun: { label: "Bugün", cls: "border-amber-500/40 bg-amber-500/10 text-amber-200", dot: "bg-amber-500" },
    takip: { label: "Takip", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", dot: "bg-emerald-500" },
};

const ONAY_FILTERS = [
    { id: "", label: "Tümü" },
    { id: "whatsapp", label: "WhatsApp" },
    { id: "otonom", label: "Otonom" },
    { id: "pazarlik", label: "Pazarlık" },
    { id: "ajan", label: "Ajan" },
    { id: "sanal_katip", label: "Sanal Katip" },
];

const AUTO_REFRESH_MS = 90000;

function formatTarih(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("tr-TR", {
            day: "2-digit",
            month: "short",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}

function Kpi({ label, value, hint, tone = "gold" }) {
    const ring =
        tone === "red"
            ? "border-rose-500/30 bg-rose-500/5"
            : tone === "emerald"
              ? "border-emerald-500/30 bg-emerald-500/5"
              : "border-[#D4AF37]/25 bg-[#121A2F]/80";
    return (
        <div className={`rounded-2xl border p-4 ${ring}`}>
            <div className="text-[0.62rem] font-bold uppercase tracking-[0.2em] text-slate-500">{label}</div>
            <div className="mt-2 font-display text-3xl font-semibold text-[#F3E5AB]">{value}</div>
            {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
        </div>
    );
}

function BandBadge({ band }) {
    const m = BAND_META[band] || BAND_META.takip;
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase ${m.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
            {m.label}
        </span>
    );
}

function MarkdownLite({ text }) {
    if (!text) return null;
    const lines = String(text).split("\n").filter(Boolean);
    return (
        <div className="space-y-2 text-sm leading-relaxed text-slate-300">
            {lines.map((line, i) => (
                <p
                    key={i}
                    dangerouslySetInnerHTML={{
                        __html: line.replace(/\*\*(.*?)\*\*/g, "<strong class='text-[#F3E5AB]'>$1</strong>"),
                    }}
                />
            ))}
        </div>
    );
}

function SkorRing({ skor }) {
    const tone =
        skor >= 80 ? "text-emerald-400 border-emerald-500/40" : skor >= 55 ? "text-amber-300 border-amber-500/40" : "text-rose-300 border-rose-500/40";
    return (
        <div className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-full border-2 font-display text-xl font-bold ${tone}`}>
            {skor}
        </div>
    );
}

export default function SupremeOpsPage() {
    const [tab, setTab] = useState("komuta");
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [running, setRunning] = useState(false);
    const [dashboard, setDashboard] = useState(null);
    const [onayItems, setOnayItems] = useState([]);
    const [oncelik, setOncelik] = useState(null);
    const [saglik, setSaglik] = useState(null);
    const [brifing, setBrifing] = useState(null);
    const [telemetri, setTelemetri] = useState(null);
    const [aktivite, setAktivite] = useState([]);
    const [expandedId, setExpandedId] = useState(null);
    const [actionId, setActionId] = useState(null);
    const [onayFilter, setOnayFilter] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [lastSync, setLastSync] = useState(null);
    const [topluSonuc, setTopluSonuc] = useState(null);
    const [warQuery, setWarQuery] = useState("");
    const [warRoom, setWarRoom] = useState(null);
    const [warLoading, setWarLoading] = useState(false);
    const refreshDebounceRef = useRef(null);

    const kpi = dashboard?.kpi || {};
    const discovery = dashboard?.discovery || {};
    const ruleSuggestions = discovery?.rule_suggestions || telemetri?.rule_suggestions || [];

    const yukle = useCallback(
        async (silent = false) => {
            if (!silent) setLoading(true);
            else setRefreshing(true);
            try {
                const [dash, onay, onc, sag, brf, tel, akt] = await Promise.all([
                    supremeOps.dashboard(),
                    supremeOps.onayKuyrugu({ limit: 50, kaynak: onayFilter || undefined }),
                    supremeOps.oncelik({ limit: 24 }),
                    supremeOps.saglik({ limit: 30 }),
                    supremeOps.brifing().catch(() => null),
                    supremeOps.telemetri().catch(() => null),
                    supremeOps.aktivite({ limit: 25 }).catch(() => ({ items: [] })),
                ]);
                setDashboard(dash);
                setOnayItems(onay?.items || []);
                setOncelik(onc);
                setSaglik(sag);
                setBrifing(brf?.success ? brf : null);
                setTelemetri(tel?.success ? tel : null);
                setAktivite(akt?.items || []);
                setLastSync(new Date());
            } catch (e) {
                toast.error(e?.response?.data?.error || e?.message || "Veri yüklenemedi");
            } finally {
                setLoading(false);
                setRefreshing(false);
            }
        },
        [onayFilter]
    );

    /** Tek onay sonrası tüm paneli değil — sadece kuyruk + KPI (Daphne yükünü düşürür). */
    const scheduleLightRefresh = useCallback(() => {
        if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        refreshDebounceRef.current = setTimeout(async () => {
            setRefreshing(true);
            try {
                const [onay, dash] = await Promise.all([
                    supremeOps.onayKuyrugu({ limit: 50, kaynak: onayFilter || undefined }),
                    supremeOps.dashboard().catch(() => null),
                ]);
                setOnayItems(onay?.items || []);
                if (dash) setDashboard(dash);
                setLastSync(new Date());
            } catch {
                /* sessiz — tam yenileme auto-refresh ile gelir */
            } finally {
                setRefreshing(false);
            }
        }, 700);
    }, [onayFilter]);

    useEffect(() => {
        return () => {
            if (refreshDebounceRef.current) clearTimeout(refreshDebounceRef.current);
        };
    }, []);

    useEffect(() => {
        yukle();
    }, [yukle]);

    useEffect(() => {
        const t = setInterval(() => yukle(true), AUTO_REFRESH_MS);
        return () => clearInterval(t);
    }, [yukle]);

    const handleOtonom = async () => {
        setRunning(true);
        try {
            const r = await supremeOps.calistir("otonom");
            const q = r?.otonom_evrak?.queued || 0;
            const u = r?.rule_engine?.uygulanan || 0;
            toast.success("Otonom motor tamamlandı", {
                description: `${q} WhatsApp kuyruğu · ${u} kural aksiyonu`,
            });
            await yukle(true);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Motor çalıştırılamadı");
        } finally {
            setRunning(false);
        }
    };

    const handleToplu = async (action) => {
        setRunning(true);
        try {
            const r = await supremeOps.topluIslem(action, action === "whatsapp_evrak_hatirlat" ? 8 : 20);
            setTopluSonuc(r);
            toast.success(r.metin || "İşlem tamamlandı");
            await yukle(true);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Toplu işlem başarısız");
        } finally {
            setRunning(false);
        }
    };

    const handleOnay = async (item, action) => {
        const key = `${item.kaynak}-${item.id}`;
        setActionId(key);
        try {
            await supremeOps.onay({ kaynak: item.kaynak, id: item.id, action });
            toast.success(action === "onayla" ? "Onaylandı" : "Reddedildi / iptal");
            setOnayItems((prev) =>
                prev.filter((x) => !(x.kaynak === item.kaynak && x.id === item.id))
            );
            scheduleLightRefresh();
        } catch (e) {
            toast.error(e?.response?.data?.error || "Onay işlemi başarısız");
        } finally {
            setActionId(null);
        }
    };

    const handleTopluOnay = async (kaynak) => {
        if (!window.confirm(`${kaynak} kaynağındaki bekleyen işleri toplu onaylamak istiyor musunuz?`)) return;
        setRunning(true);
        try {
            const r = await supremeOps.topluOnay(kaynak, 15);
            toast.success(`${r.onaylanan} kayıt onaylandı`);
            setOnayItems([]);
            scheduleLightRefresh();
            setTimeout(() => yukle(true), 1500);
        } catch (e) {
            toast.error(e?.response?.data?.error || "Toplu onay başarısız");
        } finally {
            setRunning(false);
        }
    };

    const handleSaglikCoz = async (id) => {
        try {
            await supremeOps.saglikCoz(id);
            toast.success("Uyarı kapatıldı");
            await yukle(true);
        } catch {
            toast.error("İşlem başarısız");
        }
    };

    const handleWarRoom = async (e) => {
        e?.preventDefault?.();
        const q = warQuery.trim();
        if (!q) return;
        setWarLoading(true);
        setWarRoom(null);
        try {
            const r = await supremeOps.warRoom({ q });
            setWarRoom(r);
            if (!r.success) toast.error(r.error || "Dosya bulunamadı");
        } catch (err) {
            toast.error(err?.response?.data?.error || "Savaş odası yüklenemedi");
        } finally {
            setWarLoading(false);
        }
    };

    const filteredOnay = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return onayItems;
        return onayItems.filter(
            (it) =>
                (it.baslik || "").toLowerCase().includes(q) ||
                (it.dosya_no || "").toLowerCase().includes(q) ||
                (it.plaka || "").toLowerCase().includes(q) ||
                (it.detay || "").toLowerCase().includes(q)
        );
    }, [onayItems, searchQuery]);

    const filteredOncelikBands = useMemo(() => {
        const bands = oncelik?.oncelik || {};
        const q = searchQuery.trim().toLowerCase();
        if (!q) return bands;
        const out = {};
        for (const band of ["acil", "bugun", "takip"]) {
            out[band] = (bands[band] || []).filter(
                (it) =>
                    (it.label || "").toLowerCase().includes(q) ||
                    (it.dosya_no || "").toLowerCase().includes(q) ||
                    (it.sub || "").toLowerCase().includes(q) ||
                    (it.ek_sub || "").toLowerCase().includes(q)
            );
        }
        return out;
    }, [oncelik, searchQuery]);

    if (loading && !dashboard) {
        return (
            <div className="flex min-h-[60vh] items-center justify-center text-slate-400">
                <Loader2 className="mr-2 h-6 w-6 animate-spin text-[#D4AF37]" />
                Supreme Ops yükleniyor…
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0A0F1C] text-slate-200">
            <div className="relative overflow-hidden border-b border-white/[0.06] bg-gradient-to-br from-[#0E1A2B] via-[#121A2F] to-[#1a1028]">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top_right,rgba(212,175,55,0.15),transparent_55%)]" />
                <div className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6">
                    <div className="flex flex-wrap items-start justify-between gap-4">
                        <div>
                            <p className="text-[0.62rem] font-bold uppercase tracking-[0.28em] text-[#D4AF37]/80">
                                Yeni Modül · Bağımsız Katman
                            </p>
                            <h1 className="mt-2 font-display text-3xl font-semibold text-white sm:text-4xl">MBS Supreme Ops</h1>
                            <p className="mt-2 max-w-2xl text-sm text-slate-400">
                                Sabah komuta merkezi, onay kapısı, öncelik radarı ve dosya savaş odası — mevcut modüllere dokunmadan çalışır.
                            </p>
                            {lastSync ? (
                                <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-600">
                                    <Clock className="h-3 w-3" />
                                    Son senkron: {formatTarih(lastSync.toISOString())} · otomatik yenileme 90 sn
                                </p>
                            ) : null}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <Button
                                variant="outline"
                                className="border-white/10 bg-black/20 text-slate-300 hover:bg-white/5"
                                onClick={() => yukle(true)}
                                disabled={refreshing}
                            >
                                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                                Yenile
                            </Button>
                            <Button
                                className="bg-gradient-to-r from-[#8b1f2f] to-[#6b1825] text-white hover:opacity-90"
                                onClick={handleOtonom}
                                disabled={running}
                            >
                                {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                Otonom Motor
                            </Button>
                        </div>
                    </div>

                    <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
                        <Kpi label="Acil" value={kpi.acil ?? 0} tone="red" />
                        <Kpi label="Bugün" value={kpi.bugun ?? 0} tone="gold" />
                        <Kpi label="Onay bekleyen" value={kpi.onay_bekleyen ?? 0} tone="gold" />
                        <Kpi label="Kritik dosya" value={kpi.kritik_dosya ?? 0} tone="red" />
                        <Kpi label="Sağlık uyarısı" value={kpi.saglik_uyari ?? 0} />
                        <Kpi label="Portal evrak" value={kpi.portal_evrak ?? 0} hint="Onay kuyruğu" />
                        <Kpi label="Mesaj yanıt" value={kpi.mesaj_yanit ?? 0} />
                        <Kpi label="Toplam öncelik" value={kpi.toplam_oncelik ?? 0} tone="emerald" />
                    </div>
                </div>
            </div>

            <div className="sticky top-0 z-20 border-b border-white/[0.06] bg-[#0A0F1C]/95 backdrop-blur-md">
                <div className="mx-auto flex max-w-7xl gap-1 overflow-x-auto px-4 py-2 sm:px-6">
                    {TABS.map((t) => {
                        const Icon = t.icon;
                        const count =
                            t.id === "onay"
                                ? onayItems.length
                                : t.id === "saglik"
                                  ? (saglik?.uyarilar?.length || 0) + (saglik?.kritik_dosyalar?.length || 0)
                                  : t.id === "istihbarat"
                                    ? (telemetri?.self_healing?.error_count || 0) + (ruleSuggestions?.length || 0)
                                    : null;
                        return (
                            <button
                                key={t.id}
                                type="button"
                                onClick={() => setTab(t.id)}
                                className={`flex shrink-0 items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold transition ${
                                    tab === t.id
                                        ? "bg-[#D4AF37]/15 text-[#F3E5AB] ring-1 ring-[#D4AF37]/30"
                                        : "text-slate-500 hover:bg-white/[0.04] hover:text-slate-300"
                                }`}
                            >
                                <Icon className="h-3.5 w-3.5" />
                                {t.label}
                                {count != null && count > 0 ? (
                                    <span className="rounded-full bg-rose-500/20 px-1.5 py-0.5 text-[10px] text-rose-300">{count}</span>
                                ) : null}
                            </button>
                        );
                    })}
                </div>
            </div>

            {(tab === "onay" || tab === "oncelik") && (
                <div className="border-b border-white/[0.04] bg-[#0A0F1C]/80">
                    <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-3 px-4 py-3 sm:px-6">
                        <div className="relative min-w-[200px] flex-1">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                            <input
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Dosya no, plaka veya metin ara…"
                                className="w-full rounded-xl border border-white/10 bg-black/30 py-2 pl-10 pr-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#D4AF37]/40 focus:outline-none"
                            />
                        </div>
                        {tab === "onay" ? (
                            <div className="flex flex-wrap items-center gap-1.5">
                                <Filter className="h-3.5 w-3.5 text-slate-600" />
                                {ONAY_FILTERS.map((f) => (
                                    <button
                                        key={f.id || "all"}
                                        type="button"
                                        onClick={() => setOnayFilter(f.id)}
                                        className={`rounded-lg px-2.5 py-1 text-[11px] font-semibold ${
                                            onayFilter === f.id
                                                ? "bg-[#D4AF37]/20 text-[#F3E5AB]"
                                                : "bg-white/5 text-slate-500 hover:text-slate-300"
                                        }`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        ) : null}
                    </div>
                </div>
            )}

            <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
                {tab === "komuta" && (
                    <div className="space-y-6">
                        <div className="grid gap-6 lg:grid-cols-2">
                            <section className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/60 p-5">
                                <div className="mb-4 flex items-center gap-2">
                                    <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                                    <h2 className="font-display text-lg font-semibold text-white">Günlük brifing</h2>
                                </div>
                                {brifing?.metin ? (
                                    <MarkdownLite text={brifing.metin.slice(0, 2500)} />
                                ) : (
                                    <p className="text-sm text-slate-500">Brifing yüklenemedi veya boş.</p>
                                )}
                                {brifing?.oncelik_metin ? (
                                    <div className="mt-4 border-t border-white/[0.06] pt-4">
                                        <MarkdownLite text={brifing.oncelik_metin} />
                                    </div>
                                ) : null}
                            </section>

                            <section className="space-y-4">
                                <div className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/60 p-5">
                                    <h2 className="mb-3 font-display text-lg font-semibold text-white">Top 3 öncelik</h2>
                                    <ul className="space-y-2">
                                        {(dashboard?.top3 || []).map((it, idx) => (
                                            <li key={idx}>
                                                <Link
                                                    to={it.href || `/e/sigorta-dosyalari/${it.sigorta_dosyasi_id || it.id}`}
                                                    className="flex items-center justify-between rounded-xl border border-white/[0.04] bg-black/20 px-3 py-2.5 text-sm hover:border-[#D4AF37]/30"
                                                >
                                                    <span>
                                                        <strong className="text-[#F3E5AB]">{it.label || it.dosya_no}</strong>
                                                        {it.sub || it.ek_sub ? (
                                                            <span className="mt-0.5 block text-xs text-slate-500">{it.sub || it.ek_sub}</span>
                                                        ) : null}
                                                    </span>
                                                    <ChevronRight className="h-4 w-4 text-slate-600" />
                                                </Link>
                                            </li>
                                        ))}
                                        {!dashboard?.top3?.length ? (
                                            <li className="text-sm text-slate-500">Kritik öncelik yok.</li>
                                        ) : null}
                                    </ul>
                                </div>

                                <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-5">
                                    <h2 className="mb-2 text-sm font-bold uppercase tracking-wider text-[#D4AF37]">Sistem açıklaması</h2>
                                    <ul className="space-y-1.5 text-xs text-slate-400">
                                        {(dashboard?.explainability || []).map((line, i) => (
                                            <li key={i}>• {line}</li>
                                        ))}
                                    </ul>
                                </div>
                            </section>
                        </div>

                        {brifing?.kritik_uyarilar?.length ? (
                            <section className="rounded-2xl border border-rose-500/20 bg-rose-500/5 p-5">
                                <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-rose-200">
                                    <AlertTriangle className="h-4 w-4" />
                                    Kritik uyarılar
                                </h2>
                                <ul className="space-y-1 text-sm text-slate-300">
                                    {brifing.kritik_uyarilar.slice(0, 8).map((line, i) => (
                                        <li key={i}>• {line}</li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}

                        {ruleSuggestions.length ? (
                            <section>
                                <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-white">
                                    <Lightbulb className="h-4 w-4 text-amber-400" />
                                    Akıllı kural önerileri
                                </h2>
                                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                    {ruleSuggestions.map((s) => (
                                        <div
                                            key={s.kod}
                                            className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-4"
                                        >
                                            <div className="text-[10px] font-bold uppercase tracking-wider text-amber-400/80">
                                                Güven %{Math.round((s.guven || 0) * 100)}
                                            </div>
                                            <h3 className="mt-1 font-semibold text-[#F3E5AB]">{s.baslik}</h3>
                                            <p className="mt-2 text-xs text-slate-400">{s.gerekce}</p>
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ) : null}

                        {(dashboard?.kritik_dosyalar || []).length ? (
                            <section>
                                <h2 className="mb-3 font-display text-lg text-white">Komuta — kritik dosyalar</h2>
                                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                                    {dashboard.kritik_dosyalar.map((d) => (
                                        <Link
                                            key={d.sigorta_dosyasi_id}
                                            to={`/e/sigorta-dosyalari/${d.sigorta_dosyasi_id}`}
                                            className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-black/20 px-3 py-2.5 hover:border-rose-500/20"
                                        >
                                            <div>
                                                <div className="text-sm font-medium text-slate-200">
                                                    {d.dosya_no} · {d.plaka}
                                                </div>
                                                <div className="text-xs text-slate-500">{(d.oneriler || []).join(" · ")}</div>
                                            </div>
                                            <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-xs font-bold text-rose-300">
                                                {d.skor}
                                            </span>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        ) : null}
                    </div>
                )}

                {tab === "onay" && (
                    <div className="space-y-4">
                        {onayFilter === "whatsapp" && filteredOnay.length > 0 ? (
                            <div className="flex justify-end">
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-emerald-500/30 text-emerald-300"
                                    disabled={running}
                                    onClick={() => handleTopluOnay("whatsapp")}
                                >
                                    WhatsApp toplu onay ({filteredOnay.length})
                                </Button>
                            </div>
                        ) : null}

                        {filteredOnay.length === 0 ? (
                            <div className="rounded-2xl border border-dashed border-white/10 py-16 text-center text-slate-500">
                                <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-emerald-500/50" />
                                Onay bekleyen iş yok.
                            </div>
                        ) : (
                            filteredOnay.map((item) => {
                                const key = `${item.kaynak}-${item.id}`;
                                const open = expandedId === key;
                                const busy = actionId === key;
                                return (
                                    <article
                                        key={key}
                                        className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#121A2F]/50"
                                    >
                                        <div className="flex flex-wrap items-start gap-3 p-4">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="rounded-md bg-white/5 px-2 py-0.5 text-[10px] font-bold uppercase text-[#D4AF37]">
                                                        {item.tip_label || item.kaynak}
                                                    </span>
                                                    {item.dosya_no ? (
                                                        <span className="text-xs text-slate-500">
                                                            {item.dosya_no} · {item.plaka}
                                                        </span>
                                                    ) : null}
                                                    <span className="text-[10px] text-slate-600">{formatTarih(item.tarih)}</span>
                                                </div>
                                                <h3 className="mt-1 font-semibold text-white">{item.baslik}</h3>
                                                {item.detay ? (
                                                    <p className="mt-1 line-clamp-2 text-sm text-slate-400">{item.detay}</p>
                                                ) : null}
                                                {item.href ? (
                                                    <Link to={item.href} className="mt-2 inline-flex items-center gap-1 text-xs text-[#D4AF37]/80 hover:underline">
                                                        Detaya git <ExternalLink className="h-3 w-3" />
                                                    </Link>
                                                ) : null}
                                            </div>
                                            <div className="flex shrink-0 gap-2">
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => handleOnay(item, "onayla")}
                                                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600/20 px-3 py-1.5 text-xs font-semibold text-emerald-300 ring-1 ring-emerald-500/30 hover:bg-emerald-600/30 disabled:opacity-50"
                                                >
                                                    {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
                                                    Onayla
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={busy}
                                                    onClick={() => handleOnay(item, "reddet")}
                                                    className="inline-flex items-center gap-1 rounded-lg bg-rose-600/10 px-3 py-1.5 text-xs font-semibold text-rose-300 ring-1 ring-rose-500/20 hover:bg-rose-600/20 disabled:opacity-50"
                                                >
                                                    <XCircle className="h-3 w-3" />
                                                    Red
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedId(open ? null : key)}
                                                    className="rounded-lg px-2 py-1.5 text-xs text-slate-500 hover:bg-white/5"
                                                >
                                                    {open ? "Gizle" : "Detay"}
                                                </button>
                                            </div>
                                        </div>
                                        {open ? (
                                            <div className="border-t border-white/[0.06] bg-black/30 p-4">
                                                {item.ajan_tartismalari?.length ? (
                                                    <div className="mb-3 space-y-2">
                                                        <div className="text-[10px] font-bold uppercase text-slate-500">Ajan tartışması</div>
                                                        {item.ajan_tartismalari.map((t, i) => (
                                                            <div key={i} className="rounded-lg bg-white/5 p-2 text-xs text-slate-400">
                                                                <strong className="text-[#F3E5AB]">{t.ajan || t.role}:</strong> {t.mesaj || t.content}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : null}
                                                {item.onerilen ? (
                                                    <pre className="max-h-64 overflow-auto whitespace-pre-wrap text-xs text-slate-400">
                                                        {typeof item.onerilen === "string"
                                                            ? item.onerilen
                                                            : JSON.stringify(item.onerilen, null, 2)}
                                                    </pre>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </article>
                                );
                            })
                        )}
                    </div>
                )}

                {tab === "oncelik" && (
                    <div className="grid gap-6 lg:grid-cols-3">
                        {(["acil", "bugun", "takip"]).map((band) => (
                            <section key={band} className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/40 p-4">
                                <div className="mb-4 flex items-center justify-between">
                                    <BandBadge band={band} />
                                    <span className="text-xs text-slate-500">{(filteredOncelikBands[band] || []).length} kayıt</span>
                                </div>
                                <ul className="space-y-2">
                                    {(filteredOncelikBands[band] || []).map((it, idx) => (
                                        <li key={idx}>
                                            <Link
                                                to={it.href || "#"}
                                                className="block rounded-xl border border-white/[0.04] bg-black/20 px-3 py-2 text-sm hover:border-white/10"
                                            >
                                                <div className="font-medium text-slate-200">{it.label || it.dosya_no || "—"}</div>
                                                <div className="text-xs text-slate-500">{it.sub || it.ek_sub}</div>
                                                {it.skor ? (
                                                    <div className="mt-1 text-[10px] text-[#D4AF37]">Skor {it.skor}</div>
                                                ) : null}
                                            </Link>
                                        </li>
                                    ))}
                                    {!(filteredOncelikBands[band] || []).length ? (
                                        <li className="text-xs text-slate-600">Kayıt yok</li>
                                    ) : null}
                                </ul>
                            </section>
                        ))}
                    </div>
                )}

                {tab === "istihbarat" && (
                    <div className="grid gap-6 lg:grid-cols-2">
                        <section className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/50 p-5">
                            <h2 className="mb-4 flex items-center gap-2 font-display text-lg text-white">
                                <Activity className="h-4 w-4 text-[#D4AF37]" />
                                Olay akışı (24 saat)
                            </h2>
                            <p className="mb-3 text-xs text-slate-500">
                                {telemetri?.event_stream?.event_count ?? 0} olay · pencere {telemetri?.event_stream?.window_hours ?? 24} saat
                            </p>
                            <ul className="space-y-2">
                                {(telemetri?.event_stream?.top_events || []).map((ev, i) => (
                                    <li key={i} className="flex items-center justify-between rounded-lg bg-black/20 px-3 py-2 text-sm">
                                        <span className="text-slate-300">{ev.event}</span>
                                        <span className="font-mono text-[#D4AF37]">{ev.adet}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/50 p-5">
                            <h2 className="mb-4 flex items-center gap-2 font-display text-lg text-white">
                                <Users className="h-4 w-4 text-emerald-400" />
                                WhatsApp kuyruk önceliği
                            </h2>
                            <p className="mb-3 text-xs text-slate-500">
                                Bekleyen: {telemetri?.queue_orchestrator?.pending_total ?? 0}
                            </p>
                            <ul className="max-h-72 space-y-2 overflow-y-auto">
                                {(telemetri?.queue_orchestrator?.prioritized || []).slice(0, 12).map((row) => (
                                    <li key={row.id} className="rounded-lg border border-white/[0.04] bg-black/20 px-3 py-2 text-xs">
                                        <div className="font-medium text-slate-200">{row.dosya_no || "—"} · {row.muvekkil}</div>
                                        <div className="mt-1 text-slate-500">
                                            Öncelik {row.priority_score} · risk {row.risk_score} · {row.age_min} dk
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="rounded-2xl border border-amber-500/20 bg-amber-500/5 p-5">
                            <h2 className="mb-4 flex items-center gap-2 font-display text-lg text-amber-200">
                                <AlertTriangle className="h-4 w-4" />
                                Self-healing (WhatsApp hataları)
                            </h2>
                            <p className="mb-3 text-xs text-slate-500">{telemetri?.self_healing?.error_count ?? 0} hata kaydı</p>
                            <ul className="space-y-2">
                                {(telemetri?.self_healing?.fallback_plan || []).slice(0, 8).map((a, i) => (
                                    <li key={i} className="rounded-lg bg-black/20 px-3 py-2 text-xs text-slate-400">
                                        <strong className="text-slate-200">{a.dosya_no}</strong> — {a.reason}
                                        <div className="mt-0.5 text-rose-300/80">{(a.hata || "").slice(0, 100)}</div>
                                    </li>
                                ))}
                            </ul>
                        </section>

                        <section className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/50 p-5">
                            <h2 className="mb-4 flex items-center gap-2 font-display text-lg text-white">
                                <History className="h-4 w-4 text-slate-400" />
                                Canlı aktivite
                            </h2>
                            <ul className="max-h-80 space-y-2 overflow-y-auto">
                                {aktivite.map((a) => (
                                    <li key={a.id} className="flex gap-3 rounded-lg bg-black/20 px-3 py-2 text-xs">
                                        <span
                                            className={`shrink-0 rounded px-1.5 py-0.5 font-bold uppercase ${
                                                a.tip === "otonom" ? "bg-violet-500/20 text-violet-300" : "bg-slate-500/20 text-slate-400"
                                            }`}
                                        >
                                            {a.tip}
                                        </span>
                                        <div className="min-w-0 flex-1">
                                            <div className="font-medium text-slate-200">{a.baslik}</div>
                                            {a.dosya_no ? <div className="text-slate-500">{a.dosya_no}</div> : null}
                                            <div className="text-slate-600">{formatTarih(a.tarih)}</div>
                                        </div>
                                        {a.href ? (
                                            <Link to={a.href} className="shrink-0 text-[#D4AF37]/70 hover:underline">
                                                Git
                                            </Link>
                                        ) : null}
                                    </li>
                                ))}
                                {!aktivite.length ? <li className="text-slate-500">Aktivite yok.</li> : null}
                            </ul>
                        </section>
                    </div>
                )}

                {tab === "warroom" && (
                    <div className="space-y-6">
                        <form onSubmit={handleWarRoom} className="flex flex-wrap gap-3">
                            <div className="relative min-w-[240px] flex-1">
                                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-600" />
                                <input
                                    value={warQuery}
                                    onChange={(e) => setWarQuery(e.target.value)}
                                    placeholder="Dosya no veya plaka (ör. 2024-123 veya 34 ABC 123)"
                                    className="w-full rounded-xl border border-white/10 bg-black/30 py-3 pl-10 pr-3 text-sm text-slate-200 placeholder:text-slate-600 focus:border-[#D4AF37]/40 focus:outline-none"
                                />
                            </div>
                            <Button type="submit" disabled={warLoading || !warQuery.trim()} className="bg-[#D4AF37]/20 text-[#F3E5AB]">
                                {warLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Crosshair className="h-4 w-4" />}
                                Dosyayı aç
                            </Button>
                        </form>

                        {warLoading ? (
                            <div className="flex justify-center py-16 text-slate-500">
                                <Loader2 className="h-8 w-8 animate-spin text-[#D4AF37]" />
                            </div>
                        ) : warRoom?.success ? (
                            <div className="space-y-6">
                                <DosyaIntelPanel dosyaId={warRoom.dosya.id} />
                            <div className="grid gap-6 lg:grid-cols-3">
                                <section className="lg:col-span-2 rounded-2xl border border-white/[0.06] bg-[#121A2F]/60 p-6">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div>
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-[#D4AF37]">Savaş odası</p>
                                            <h2 className="mt-1 font-display text-2xl font-semibold text-white">{warRoom.dosya.dosya_no}</h2>
                                            <p className="mt-1 text-sm text-slate-400">
                                                {warRoom.dosya.plaka} · {warRoom.dosya.marka} {warRoom.dosya.model} · {warRoom.dosya.muvekkil}
                                            </p>
                                            <p className="mt-1 text-xs text-slate-500">
                                                {warRoom.dosya.dosya_durumu_label} · {warRoom.dosya.basvuru_konusu} · {warRoom.dosya.sigorta_sirketi}
                                            </p>
                                        </div>
                                        <SkorRing skor={warRoom.saglik?.skor ?? 0} />
                                    </div>
                                    <div className="mt-6 flex flex-wrap gap-2">
                                        <Link
                                            to={warRoom.dosya.href}
                                            className="inline-flex items-center gap-1 rounded-lg bg-[#D4AF37]/15 px-3 py-2 text-xs font-semibold text-[#F3E5AB] hover:bg-[#D4AF37]/25"
                                        >
                                            Dosyayı düzenle <ExternalLink className="h-3 w-3" />
                                        </Link>
                                        <button
                                            type="button"
                                            className="rounded-lg bg-white/5 px-3 py-2 text-xs text-slate-400 hover:bg-white/10"
                                            onClick={() => {
                                                setTab("onay");
                                                setSearchQuery(warRoom.dosya.dosya_no || "");
                                            }}
                                        >
                                            Onay kuyruğunda ara
                                        </button>
                                    </div>
                                    {warRoom.oncelik ? (
                                        <div className="mt-6 rounded-xl border border-[#D4AF37]/20 bg-[#D4AF37]/5 p-4">
                                            <div className="flex items-center gap-2">
                                                <BandBadge band={warRoom.oncelik.band} />
                                                <span className="text-sm text-slate-300">Öncelik skoru: {warRoom.oncelik.skor}</span>
                                            </div>
                                            <p className="mt-2 text-xs text-slate-400">{warRoom.oncelik.sub || warRoom.oncelik.ek_sub}</p>
                                        </div>
                                    ) : null}
                                    {warRoom.eksik_evrak?.length ? (
                                        <div className="mt-4 rounded-xl border border-amber-500/20 bg-amber-500/5 p-4">
                                            <div className="text-xs font-bold uppercase text-amber-400">Eksik evrak</div>
                                            <p className="mt-1 text-sm text-slate-300">{warRoom.eksik_evrak.join(", ")}</p>
                                        </div>
                                    ) : null}
                                    {(warRoom.saglik?.oneriler || []).length ? (
                                        <ul className="mt-4 space-y-1 text-sm text-slate-400">
                                            {warRoom.saglik.oneriler.map((o, i) => (
                                                <li key={i}>• {o}</li>
                                            ))}
                                        </ul>
                                    ) : null}
                                </section>

                                <section className="space-y-4">
                                    <div className="rounded-2xl border border-white/[0.06] bg-black/20 p-4">
                                        <h3 className="text-sm font-semibold text-white">Onay bekleyen</h3>
                                        <ul className="mt-2 space-y-2">
                                            {(warRoom.onay_bekleyen || []).map((o) => (
                                                <li key={`${o.kaynak}-${o.id}`} className="text-xs text-slate-400">
                                                    <span className="text-[#F3E5AB]">{o.tip}</span> — {o.baslik}
                                                </li>
                                            ))}
                                            {!warRoom.onay_bekleyen?.length ? (
                                                <li className="text-slate-600">Bekleyen onay yok</li>
                                            ) : null}
                                        </ul>
                                    </div>
                                    <div className="rounded-2xl border border-white/[0.06] bg-black/20 p-4">
                                        <h3 className="text-sm font-semibold text-white">Sağlık uyarıları</h3>
                                        <ul className="mt-2 space-y-2">
                                            {(warRoom.uyarilar || []).map((u) => (
                                                <li key={u.id} className="text-xs text-slate-400">
                                                    <span className={u.seviye === "danger" ? "text-rose-300" : "text-amber-300"}>{u.baslik}</span>
                                                    <div>{u.aciklama}</div>
                                                </li>
                                            ))}
                                            {!warRoom.uyarilar?.length ? <li className="text-slate-600">Uyarı yok</li> : null}
                                        </ul>
                                    </div>
                                </section>
                            </div>
                            </div>
                        ) : (
                            <div className="rounded-2xl border border-dashed border-white/10 py-20 text-center text-slate-500">
                                <Crosshair className="mx-auto mb-3 h-10 w-10 opacity-40" />
                                Dosya no veya plaka girerek tek ekranda özet görün.
                            </div>
                        )}
                    </div>
                )}

                {tab === "toplu" && (
                    <div className="space-y-6">
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[
                                {
                                    id: "eksik_evrak_listele",
                                    title: "Eksik evrak taraması",
                                    desc: "Açık dosyalarda 7'li evrak kuralına göre eksikleri listeler.",
                                    icon: ListChecks,
                                },
                                {
                                    id: "whatsapp_evrak_hatirlat",
                                    title: "Toplu WhatsApp hatırlatma",
                                    desc: "Eksik evraklı dosyalara otomatik mesaj kuyruğu ekler.",
                                    icon: Users,
                                },
                                {
                                    id: "saglik_tarama",
                                    title: "Gece sağlık taraması",
                                    desc: "Tüm aktif dosyalarda sağlık uyarısı üretir (manuel tetik).",
                                    icon: Activity,
                                    motor: "saglik",
                                },
                                {
                                    id: "yatar_bedeli",
                                    title: "Yatar bedeli hesapla",
                                    desc: "Seçerek veya toplu mahrumiyet PDF üretir. Dosya kaydında otomatik çalışmaz.",
                                    icon: Car,
                                    href: "/yeni/yatar-bedeli-merkezi",
                                },
                            ].map((card) => {
                                const Icon = card.icon;
                                return (
                                    <div
                                        key={card.id}
                                        className="flex flex-col rounded-2xl border border-white/[0.06] bg-gradient-to-b from-[#121A2F] to-[#0A0F1C] p-5"
                                    >
                                        <Icon className="h-8 w-8 text-[#D4AF37]/80" />
                                        <h3 className="mt-4 font-display text-lg font-semibold text-white">{card.title}</h3>
                                        <p className="mt-2 flex-1 text-sm text-slate-400">{card.desc}</p>
                                        <Button
                                            className="mt-4 w-full bg-[#D4AF37]/15 text-[#F3E5AB] hover:bg-[#D4AF37]/25"
                                            disabled={running && !card.href}
                                            onClick={async () => {
                                                if (card.href) {
                                                    window.location.href = card.href;
                                                    return;
                                                }
                                                if (card.motor === "saglik") {
                                                    setRunning(true);
                                                    try {
                                                        const r = await supremeOps.calistir("saglik");
                                                        setTopluSonuc(r);
                                                        toast.success("Sağlık taraması tamamlandı");
                                                        await yukle(true);
                                                    } catch {
                                                        toast.error("Tarama başarısız");
                                                    } finally {
                                                        setRunning(false);
                                                    }
                                                } else {
                                                    handleToplu(card.id);
                                                }
                                            }}
                                        >
                                            {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                                            Çalıştır
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>

                        {topluSonuc?.items?.length ? (
                            <section className="rounded-2xl border border-white/[0.06] bg-[#121A2F]/50 p-5">
                                <h3 className="mb-3 font-display text-lg text-white">Son işlem sonucu</h3>
                                {topluSonuc.metin ? <MarkdownLite text={topluSonuc.metin} /> : null}
                                <ul className="mt-4 space-y-2">
                                    {topluSonuc.items.slice(0, 15).map((it, i) => (
                                        <li key={i}>
                                            <Link
                                                to={it.href || "#"}
                                                className="block rounded-lg border border-white/[0.04] bg-black/20 px-3 py-2 text-sm hover:border-white/10"
                                            >
                                                <div className="font-medium text-slate-200">{it.label}</div>
                                                <div className="text-xs text-slate-500">{it.sub || it.status}</div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}
                    </div>
                )}

                {tab === "saglik" && (
                    <div className="grid gap-6 lg:grid-cols-2">
                        <section>
                            <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-white">
                                <AlertTriangle className="h-4 w-4 text-amber-400" />
                                Aktif uyarılar
                            </h2>
                            <ul className="space-y-2">
                                {(saglik?.uyarilar || []).map((u) => (
                                    <li
                                        key={u.id}
                                        className={`rounded-xl border p-3 ${
                                            u.seviye === "danger"
                                                ? "border-rose-500/30 bg-rose-500/5"
                                                : u.seviye === "warning"
                                                  ? "border-amber-500/30 bg-amber-500/5"
                                                  : "border-white/10 bg-black/20"
                                        }`}
                                    >
                                        <div className="flex justify-between gap-2">
                                            <div>
                                                <div className="text-sm font-semibold text-white">{u.baslik}</div>
                                                <div className="text-xs text-slate-500">
                                                    {u.dosya_no} {u.plaka ? `· ${u.plaka}` : ""}
                                                </div>
                                                <p className="mt-1 text-xs text-slate-400">{u.aciklama}</p>
                                                {u.href ? (
                                                    <Link to={u.href} className="mt-1 inline-block text-[10px] text-[#D4AF37]/80 hover:underline">
                                                        Dosyaya git
                                                    </Link>
                                                ) : null}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => handleSaglikCoz(u.id)}
                                                className="shrink-0 text-[10px] font-bold uppercase text-emerald-400 hover:underline"
                                            >
                                                Çözüldü
                                            </button>
                                        </div>
                                    </li>
                                ))}
                                {!(saglik?.uyarilar || []).length ? (
                                    <li className="text-sm text-slate-500">Aktif uyarı yok.</li>
                                ) : null}
                            </ul>
                        </section>
                        <section>
                            <h2 className="mb-3 flex items-center gap-2 font-display text-lg text-white">
                                <FileWarning className="h-4 w-4 text-rose-400" />
                                Kritik dosyalar (skor &lt; 60)
                            </h2>
                            <ul className="space-y-2">
                                {(saglik?.kritik_dosyalar || []).map((d) => (
                                    <li key={d.sigorta_dosyasi_id}>
                                        <Link
                                            to={`/e/sigorta-dosyalari/${d.sigorta_dosyasi_id}`}
                                            className="flex items-center justify-between rounded-xl border border-white/[0.06] bg-black/20 px-3 py-2.5 hover:border-rose-500/20"
                                        >
                                            <div>
                                                <div className="text-sm font-medium text-slate-200">
                                                    {d.dosya_no} · {d.plaka}
                                                </div>
                                                <div className="text-xs text-slate-500">{(d.oneriler || []).join(" · ")}</div>
                                            </div>
                                            <span className="rounded-full bg-rose-500/20 px-2 py-0.5 text-xs font-bold text-rose-300">
                                                {d.skor}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    </div>
                )}
            </div>
        </div>
    );
}
